import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Save, Fuel, Thermometer, Scale, Circle, Clock, Cloud, Wind, Droplet, Download, Trophy, Activity, Video } from 'lucide-react';
import { API } from '../config/api';
import AnalysisImportDialog from './AnalysisImportDialog';

const FUEL_DENSITY_KG_PER_L = 0.74;

// Large, iPad-friendly number input. Prevents iOS auto-zoom by using 16px
// base font and `inputMode="decimal"`.
const NumInput = ({ value, onChange, placeholder, testId, className = '' }) => (
  <Input
    inputMode="decimal"
    type="number"
    step="0.1"
    placeholder={placeholder}
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
    data-testid={testId}
    className={`h-11 text-base bg-neutral-800 border-neutral-700 text-white ${className}`}
  />
);

// Numeric-only text input. Strips non-digits as the user types, perfect for
// tire serials. Allows leading zeros and arbitrary length.
const NumStringInput = ({ value, onChange, placeholder, testId, className = '' }) => (
  <Input
    inputMode="numeric"
    pattern="[0-9]*"
    placeholder={placeholder}
    value={value || ''}
    onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
    data-testid={testId}
    className={`h-11 text-base bg-neutral-800 border-neutral-700 text-white font-mono ${className}`}
  />
);

const TextLargeInput = ({ value, onChange, placeholder, testId, className = '' }) => (
  <Input
    placeholder={placeholder}
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
    className={`h-11 text-base bg-neutral-800 border-neutral-700 text-white ${className}`}
  />
);

// One tire set (Set 1 or Set 2): 4 serial inputs in 2x2 grid + front/rear axle
// pressures. Designed to be thumb-friendly on an iPad in portrait.
const TireSet = ({ setNum, data, onChange, tandaKey, onAutofill, autofillLabel }) => {
  const d = data || {};
  const up = (patch) => onChange({ ...d, ...patch });

  return (
    <Card className="bg-neutral-900 border-neutral-800">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h4 className="text-sm font-semibold text-white flex items-center gap-2">
            <Circle className="w-4 h-4 text-orange-500" />
            Set {setNum}
          </h4>
          <div className="flex items-center gap-1.5">
            {onAutofill && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onAutofill}
                data-testid={`autofill-tires-${tandaKey}-set${setNum}`}
                className="h-7 text-[11px] border-orange-700 text-orange-400 hover:bg-orange-950 px-2"
                title={`Copiar seriales y presiones desde ${autofillLabel}`}
              >
                = {autofillLabel}
              </Button>
            )}
            {setNum === 2 && <Badge variant="outline" className="text-[10px]">Opcional</Badge>}
          </div>
        </div>

        {/* 2x2 serial grid - FL/FR on top, RL/RR on bottom */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[11px] text-gray-500">Delantero Izq.</Label>
            <NumStringInput
              value={d.FL_serial}
              onChange={(v) => up({ FL_serial: v })}
              placeholder="# serial"
              testId={`tire-${tandaKey}-set${setNum}-FL`}
            />
          </div>
          <div>
            <Label className="text-[11px] text-gray-500">Delantero Der.</Label>
            <NumStringInput
              value={d.FR_serial}
              onChange={(v) => up({ FR_serial: v })}
              placeholder="# serial"
              testId={`tire-${tandaKey}-set${setNum}-FR`}
            />
          </div>
          <div>
            <Label className="text-[11px] text-gray-500">Trasero Izq.</Label>
            <NumStringInput
              value={d.RL_serial}
              onChange={(v) => up({ RL_serial: v })}
              placeholder="# serial"
              testId={`tire-${tandaKey}-set${setNum}-RL`}
            />
          </div>
          <div>
            <Label className="text-[11px] text-gray-500">Trasero Der.</Label>
            <NumStringInput
              value={d.RR_serial}
              onChange={(v) => up({ RR_serial: v })}
              placeholder="# serial"
              testId={`tire-${tandaKey}-set${setNum}-RR`}
            />
          </div>
        </div>

        {/* Axle pressures — 2 cols: Delantero / Trasero; 2 rows: Entrada/Salida */}
        <div className="pt-3 border-t border-neutral-800 grid grid-cols-2 gap-2">
          <div className="space-y-2">
            <p className="text-xs font-semibold text-orange-400 uppercase tracking-wide">Tren Delantero (Lbs)</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] text-gray-500">Entrada</Label>
                <NumInput value={d.front_lbs_in} onChange={(v) => up({ front_lbs_in: v })} placeholder="Lbs" />
              </div>
              <div>
                <Label className="text-[11px] text-gray-500">Salida</Label>
                <NumInput value={d.front_lbs_out} onChange={(v) => up({ front_lbs_out: v })} placeholder="Lbs" />
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-xs font-semibold text-orange-400 uppercase tracking-wide">Tren Trasero (Lbs)</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] text-gray-500">Entrada</Label>
                <NumInput value={d.rear_lbs_in} onChange={(v) => up({ rear_lbs_in: v })} placeholder="Lbs" />
              </div>
              <div>
                <Label className="text-[11px] text-gray-500">Salida</Label>
                <NumInput value={d.rear_lbs_out} onChange={(v) => up({ rear_lbs_out: v })} placeholder="Lbs" />
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function TandaForm({
  tandaKey,
  tandaLabel,
  tandaData,
  onSave,
  baseCarWeight,
  regulatoryWeight,
  regulatoryBallast,
  hideRefTime = false,
  eventId,
  eventDate,
  eventCategory,
  eventCircuit,
  pilotName,
  previousTandaData,
  previousTandaLabel,
}) {
  const [bestTime, setBestTime] = useState(tandaData.best_time || '');
  const [refTime, setRefTime] = useState(tandaData.ref_time || '');
  const [position, setPosition] = useState(tandaData.position ?? '');
  const [tandaTime, setTandaTime] = useState(tandaData.tanda_time || '');
  const [observations, setObservations] = useState(tandaData.observations || '');
  const [changesNext, setChangesNext] = useState(tandaData.changes_next || '');
  const [tires, setTires] = useState(tandaData.tires || { set1: {}, set2: {} });
  const [fuel, setFuel] = useState(tandaData.fuel || {});
  const [weights, setWeights] = useState(tandaData.weights || {});
  const [temps, setTemps] = useState(tandaData.temps || {});
  const [weather, setWeather] = useState(tandaData.weather || null);
  const [savedAt, setSavedAt] = useState(tandaData.saved_at || null);
  const [analysisLink, setAnalysisLink] = useState(tandaData.analysis_link || null);
  const [fetchingWeather, setFetchingWeather] = useState(false);
  const [showImport, setShowImport] = useState(false);

  useEffect(() => {
    setBestTime(tandaData.best_time || '');
    setRefTime(tandaData.ref_time || '');
    setPosition(tandaData.position ?? '');
    setTandaTime(tandaData.tanda_time || '');
    setObservations(tandaData.observations || '');
    setChangesNext(tandaData.changes_next || '');
    setTires(tandaData.tires || { set1: {}, set2: {} });
    setFuel(tandaData.fuel || {});
    setWeights(tandaData.weights || {});
    setTemps(tandaData.temps || {});
    setWeather(tandaData.weather || null);
    setSavedAt(tandaData.saved_at || null);
    setAnalysisLink(tandaData.analysis_link || null);
  }, [tandaKey, tandaData]);

  // Fetch weather. If `forDateTime` is provided (YYYY-MM-DD, HH:MM) we hit the
  // historical endpoint so the captured weather matches when the tanda was
  // actually run on track — not the moment the user hit Save.
  const fetchWeather = async (forDateTime = null) => {
    if (!navigator.geolocation) {
      toast.error('Tu dispositivo no soporta geolocalización');
      return null;
    }
    setFetchingWeather(true);
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const { latitude, longitude } = pos.coords;
            const isHistorical = !!(forDateTime && forDateTime.date && forDateTime.time);
            const url = isHistorical ? `${API}/pit/weather/historical` : `${API}/pit/weather`;
            const params = isHistorical
              ? { lat: latitude, lon: longitude, date: forDateTime.date, time: forDateTime.time }
              : { lat: latitude, lon: longitude };
            const res = await axios.get(url, { params, withCredentials: true });
            setWeather(res.data);
            toast.success(
              isHistorical
                ? `Clima histórico: ${res.data.temp}°C (${forDateTime.time})`
                : `Clima: ${res.data.temp}°C`,
              { duration: 1500 },
            );
            resolve(res.data);
          } catch (err) {
            const detail = err.response?.data?.detail || 'No se pudo obtener el clima';
            toast.error(detail);
            resolve(null);
          } finally {
            setFetchingWeather(false);
          }
        },
        () => {
          setFetchingWeather(false);
          toast.error('Permiso de ubicación denegado');
          resolve(null);
        },
        { timeout: 8000, maximumAge: 60000 }
      );
    });
  };

  // Fuel calculations
  const consumoTotal = useMemo(() => {
    const s = Number(fuel.salida), e = Number(fuel.entrada);
    if (!Number.isFinite(s) || !Number.isFinite(e)) return null;
    return +(s - e).toFixed(2);
  }, [fuel.salida, fuel.entrada]);

  const avgLaps = useMemo(() => {
    const fast = Number(fuel.fast_laps) || 0;
    const slow = Number(fuel.slow_laps) || 0;
    if (fast === 0 && slow === 0) return null;
    return +(fast + slow * 0.7).toFixed(2);
  }, [fuel.fast_laps, fuel.slow_laps]);

  const consumoPorAvgLap = useMemo(() => {
    if (consumoTotal === null || !avgLaps) return null;
    return +(consumoTotal / avgLaps).toFixed(3);
  }, [consumoTotal, avgLaps]);

  // Weight calculations
  const effectiveBase = weights.override ?? baseCarWeight;
  const effectiveRegulatory = weights.regulatory_override ?? regulatoryWeight;
  const effectiveBallast = weights.ballast_override ?? regulatoryBallast;
  const pesoSalida = useMemo(() => {
    if (!Number.isFinite(Number(effectiveBase)) || !Number.isFinite(Number(fuel.salida))) return null;
    return +(Number(effectiveBase) + Number(fuel.salida) * FUEL_DENSITY_KG_PER_L).toFixed(2);
  }, [effectiveBase, fuel.salida]);
  const pesoEntrada = useMemo(() => {
    if (!Number.isFinite(Number(effectiveBase)) || !Number.isFinite(Number(fuel.entrada))) return null;
    return +(Number(effectiveBase) + Number(fuel.entrada) * FUEL_DENSITY_KG_PER_L).toFixed(2);
  }, [effectiveBase, fuel.entrada]);

  // Margin against the regulatory minimum at session-end (worst case).
  // Positive = above minimum (legal), negative = below minimum (illegal).
  const margenReglamentario = useMemo(() => {
    if (pesoEntrada === null || !Number.isFinite(Number(effectiveRegulatory))) return null;
    return +(pesoEntrada - Number(effectiveRegulatory)).toFixed(2);
  }, [pesoEntrada, effectiveRegulatory]);

  // Race-need fuel calculation: if user knows how many litres are needed to
  // complete the race, compute the remaining at the end + the weight the car
  // will arrive at scrutineering ("técnica").
  const raceNeeded = useMemo(() => {
    const n = Number(fuel.race_needed);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [fuel.race_needed]);
  const remanenteTecnica = useMemo(() => {
    const s = Number(fuel.salida);
    if (raceNeeded === null || !Number.isFinite(s)) return null;
    return +(s - raceNeeded).toFixed(2);
  }, [fuel.salida, raceNeeded]);
  const pesoTecnica = useMemo(() => {
    if (remanenteTecnica === null || !Number.isFinite(Number(effectiveBase))) return null;
    return +(Number(effectiveBase) + remanenteTecnica * FUEL_DENSITY_KG_PER_L).toFixed(2);
  }, [remanenteTecnica, effectiveBase]);

  const updateSet = (setKey, data) => setTires({ ...tires, [setKey]: data });

  const autofillTireSet = (setKey) => {
    const prevTires = (previousTandaData || {}).tires || {};
    const src = prevTires[setKey] || {};
    const hasAny = ['FL_serial', 'FR_serial', 'RL_serial', 'RR_serial',
                    'front_lbs_in', 'front_lbs_out', 'rear_lbs_in', 'rear_lbs_out']
                    .some((k) => src[k] !== undefined && src[k] !== null && src[k] !== '');
    if (!hasAny) {
      toast.warning(`${previousTandaLabel} no tiene datos de cubiertas cargados`);
      return;
    }
    setTires({ ...tires, [setKey]: { ...(tires[setKey] || {}), ...src } });
    toast.success(`Copiado desde ${previousTandaLabel}`, { duration: 1500 });
  };

  const handleSave = async () => {
    // Default the tanda time to the current local hour if the user didn't fill
    // it in. This is the time we'll use to look up the historical weather.
    let usedTandaTime = tandaTime;
    if (!usedTandaTime) {
      const d = new Date();
      usedTandaTime = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      setTandaTime(usedTandaTime);
    }

    // Only fetch weather the FIRST time the tanda is saved (or whenever the
    // user explicitly hit the Clima button and cleared it). Use the historical
    // endpoint with event.date + tanda_time so the value matches when the
    // tanda actually ran on the track, not when the engineer typed it in.
    let nextWeather = weather;
    if (!nextWeather && eventDate && usedTandaTime) {
      nextWeather = await fetchWeather({ date: eventDate, time: usedTandaTime });
    }

    const payload = {
      best_time: bestTime,
      ref_time: refTime,
      position: position === '' || position === null ? null : Number(position),
      tanda_time: usedTandaTime,
      observations,
      changes_next: changesNext,
      tires,
      fuel,
      weights,
      temps,
      saved_at: true,           // backend interprets `true` as "stamp now"
    };
    if (nextWeather) payload.weather = nextWeather;
    onSave(payload);
    setSavedAt(new Date().toISOString());
  };

  const handleImported = (newTanda) => {
    if (!newTanda) return;
    // Sync local state from server-confirmed tanda payload
    setBestTime(newTanda.best_time || '');
    setRefTime(newTanda.ref_time || '');
    setPosition(newTanda.position ?? '');
    setFuel(newTanda.fuel || {});
    if (newTanda.tanda_time) setTandaTime(newTanda.tanda_time);
    if (newTanda.weather) setWeather(newTanda.weather);
    setAnalysisLink(newTanda.analysis_link || null);
    setSavedAt(newTanda.saved_at || new Date().toISOString());
  };

  return (
    <div className="space-y-4 pb-20">
      {/* Sticky save bar */}
      <div className="flex items-center justify-between sticky top-[60px] z-30 bg-neutral-950/95 backdrop-blur py-3 -mx-3 px-3 border-b border-neutral-800 gap-2 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-xl font-bold">{tandaLabel}</h2>
          <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
            <Clock className="w-3 h-3" />
            <span>Mejor: <span className="text-green-400 font-mono">{bestTime || '—'}</span></span>
            {savedAt && (
              <span className="text-gray-400">
                · Guardada {new Date(savedAt).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            {weather && (
              <span className="text-gray-400 flex items-center gap-1">
                · <Cloud className="w-3 h-3 text-blue-400" /> {weather.temp}°C
                <Wind className="w-3 h-3 text-cyan-400 ml-1" /> {weather.wind} km/h
                <Droplet className="w-3 h-3 text-blue-300 ml-1" /> {weather.humidity}%
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {eventId && (
            <Button
              onClick={() => setShowImport(true)}
              variant="outline"
              size="sm"
              className="h-11 text-sm border-blue-700 text-blue-400 hover:bg-blue-950"
              data-testid={`analysis-import-btn-${tandaKey}`}
            >
              <Download className="w-4 h-4 mr-1" />
              Análisis
            </Button>
          )}
          {eventId && (
            <Button
              onClick={() => window.open(`/telemetry?event=${eventId}&tanda=${tandaKey}`, '_blank')}
              variant="outline"
              size="sm"
              className="h-11 text-sm border-green-700 text-green-400 hover:bg-green-950"
              data-testid={`telemetry-btn-${tandaKey}`}
              title="Subir / linkear archivo de telemetría (VBO/CSV)"
            >
              <Activity className="w-4 h-4 mr-1" />
              DAQ
            </Button>
          )}
          {eventId && (
            <Button
              onClick={() => window.open(`/replay/${eventId}/${tandaKey}`, '_blank')}
              variant="outline"
              size="sm"
              className="h-11 text-sm border-red-700 text-red-400 hover:bg-red-950"
              data-testid={`replay-btn-${tandaKey}`}
              title="Video onboard + telemetría sincronizada"
            >
              <Video className="w-4 h-4 mr-1" />
              Replay
            </Button>
          )}
          <Button
            onClick={() => {
              if (eventDate && tandaTime) {
                fetchWeather({ date: eventDate, time: tandaTime });
              } else {
                fetchWeather();
              }
            }}
            disabled={fetchingWeather}
            variant="outline"
            size="sm"
            className="h-11 text-sm"
            data-testid={`fetch-weather-${tandaKey}`}
          >
            <Cloud className="w-4 h-4 mr-1" />
            {fetchingWeather ? '...' : 'Clima'}
          </Button>
          <Button
            onClick={handleSave}
            data-testid={`save-tanda-${tandaKey}`}
            className="bg-red-600 hover:bg-red-700 h-11 text-base px-6"
          >
            <Save className="w-4 h-4 mr-1.5" />
            Guardar
          </Button>
        </div>
      </div>

      {/* Imported-from banner */}
      {analysisLink && (
        <div className="flex items-center gap-2 text-[11px] text-blue-300 bg-blue-950/30 border border-blue-900/40 rounded px-3 py-1.5">
          <Trophy className="w-3.5 h-3.5 flex-shrink-0" />
          <span>
            Datos importados del análisis: <span className="text-white font-semibold">{analysisLink.race_name}</span>
            {analysisLink.race_date && <span className="text-gray-400"> · {analysisLink.race_date}</span>}
            {analysisLink.driver_name && <span className="text-gray-400"> · piloto matched: {analysisLink.driver_name}</span>}
          </span>
        </div>
      )}

      {/* Best time, reference, position & tanda time */}
      <Card className="bg-neutral-900 border-neutral-800">
        <CardContent className={`p-4 grid gap-3 ${hideRefTime ? 'grid-cols-3' : 'grid-cols-4'}`}>
          <div>
            <Label className="text-xs text-gray-400">Mejor tiempo</Label>
            <TextLargeInput
              value={bestTime}
              onChange={setBestTime}
              placeholder="1:20.456"
              testId={`best-${tandaKey}`}
              className="font-mono text-lg"
            />
          </div>
          {!hideRefTime && (
            <div>
              <Label className="text-xs text-gray-400">Referencia</Label>
              <TextLargeInput
                value={refTime}
                onChange={setRefTime}
                placeholder="1:20.000"
                className="font-mono text-lg"
              />
            </div>
          )}
          <div>
            <Label className="text-xs text-gray-400">Posición</Label>
            <Input
              type="number"
              inputMode="numeric"
              min="1"
              value={position ?? ''}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="—"
              data-testid={`position-${tandaKey}`}
              className="h-11 text-lg bg-neutral-800 border-neutral-700 text-white font-mono"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-400">Hora de la tanda</Label>
            <Input
              type="time"
              value={tandaTime}
              onChange={(e) => setTandaTime(e.target.value)}
              data-testid={`tanda-time-${tandaKey}`}
              className="h-11 text-lg bg-neutral-800 border-neutral-700 text-white font-mono"
              placeholder="HH:MM"
            />
            <p className="text-[10px] text-gray-500 mt-1">Usado para clima histórico</p>
          </div>
        </CardContent>
      </Card>

      {/* DAQ-derived metrics (auto-filled when telemetry is linked) */}
      {(tandaData.daq || tandaData.telemetry_link) && (
        <Card className="bg-green-950/20 border-green-700/30">
          <CardContent className="p-4 space-y-2">
            <h3 className="text-sm font-semibold flex items-center gap-2 text-green-300">
              <Activity className="w-4 h-4" />
              Datos de adquisición
              {tandaData.telemetry_link?.session_name && (
                <span className="text-[11px] text-gray-400 font-normal ml-1">
                  · {tandaData.telemetry_link.session_name} (vuelta {tandaData.telemetry_link.lap_number})
                </span>
              )}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              <div className="bg-neutral-950 rounded-md px-2 py-1.5">
                <div className="text-[10px] text-gray-500 uppercase">Velocidad máxima</div>
                <div className="font-mono text-white text-base" data-testid={`daq-max-speed-${tandaKey}`}>
                  {tandaData.daq?.max_speed_kmh ?? '—'} <span className="text-[10px] text-gray-500">km/h</span>
                </div>
              </div>
              <div className="bg-neutral-950 rounded-md px-2 py-1.5">
                <div className="text-[10px] text-gray-500 uppercase">RPM máximo</div>
                <div className="font-mono text-white text-base">
                  {tandaData.daq?.max_rpm ?? '—'} <span className="text-[10px] text-gray-500">rpm</span>
                </div>
              </div>
              <div className="bg-neutral-950 rounded-md px-2 py-1.5">
                <div className="text-[10px] text-gray-500 uppercase">Vueltas</div>
                <div className="font-mono text-white text-base">{tandaData.daq?.lap_count ?? '—'}</div>
              </div>
              <div className="bg-neutral-950 rounded-md px-2 py-1.5">
                <div className="text-[10px] text-gray-500 uppercase">Distancia total</div>
                <div className="font-mono text-white text-base">
                  {tandaData.daq?.distance_m ? (tandaData.daq.distance_m / 1000).toFixed(2) : '—'}
                  <span className="text-[10px] text-gray-500"> km</span>
                </div>
              </div>
              <div className="bg-neutral-950 rounded-md px-2 py-1.5">
                <div className="text-[10px] text-gray-500 uppercase">G lat. acumulada</div>
                <div className="font-mono text-white text-base">
                  {tandaData.daq?.lat_g_accumulated ?? '—'} <span className="text-[10px] text-gray-500">g·s</span>
                </div>
              </div>
              {tandaData.telemetry_link?.session_id && (
                <a
                  href={`/telemetry?open=${tandaData.telemetry_link.session_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="bg-neutral-950 rounded-md px-2 py-1.5 flex items-center justify-center text-blue-400 hover:bg-neutral-900 text-xs"
                  data-testid={`daq-view-${tandaKey}`}
                >
                  Ver telemetría →
                </a>
              )}
            </div>
            <p className="text-[10px] text-gray-500">
              Los campos arriba (best, vueltas rápidas/lentas, agua, sonda) se llenaron automáticamente desde la telemetría. Podés editarlos a mano si necesitás.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Tires: Set 1 + Set 2 side-by-side on iPad, stacked on mobile */}
      <div className="grid lg:grid-cols-2 gap-3">
        <TireSet
          setNum={1}
          data={tires.set1}
          onChange={(d) => updateSet('set1', d)}
          tandaKey={tandaKey}
          onAutofill={previousTandaData ? () => autofillTireSet('set1') : null}
          autofillLabel={previousTandaLabel}
        />
        <TireSet
          setNum={2}
          data={tires.set2}
          onChange={(d) => updateSet('set2', d)}
          tandaKey={tandaKey}
          onAutofill={previousTandaData ? () => autofillTireSet('set2') : null}
          autofillLabel={previousTandaLabel}
        />
      </div>

      {/* Fuel */}
      <Card className="bg-neutral-900 border-neutral-800">
        <CardContent className="p-4 space-y-3">
          <h3 className="text-base font-semibold flex items-center gap-2">
            <Fuel className="w-4 h-4 text-yellow-500" />
            Combustible (L)
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-400">Salida (inicial)</Label>
              <NumInput
                value={fuel.salida}
                onChange={(v) => setFuel({ ...fuel, salida: v })}
                placeholder="Litros"
                testId={`fuel-salida-${tandaKey}`}
              />
            </div>
            <div>
              <Label className="text-xs text-gray-400">Entrada (final)</Label>
              <NumInput
                value={fuel.entrada}
                onChange={(v) => setFuel({ ...fuel, entrada: v })}
                placeholder="Litros"
                testId={`fuel-entrada-${tandaKey}`}
              />
            </div>
            <div>
              <Label className="text-xs text-gray-400">Vueltas rápidas (x1.0)</Label>
              <NumInput
                value={fuel.fast_laps}
                onChange={(v) => setFuel({ ...fuel, fast_laps: v })}
                placeholder="Cantidad"
                testId={`fast-laps-${tandaKey}`}
              />
            </div>
            <div>
              <Label className="text-xs text-gray-400">Vueltas lentas (x0.7)</Label>
              <NumInput
                value={fuel.slow_laps}
                onChange={(v) => setFuel({ ...fuel, slow_laps: v })}
                placeholder="Cantidad"
                testId={`slow-laps-${tandaKey}`}
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 pt-3 border-t border-neutral-800 text-center">
            <div className="bg-neutral-950 rounded-md p-2">
              <div className="text-[10px] text-gray-500 uppercase">Consumo total</div>
              <div className="font-mono text-yellow-400 text-lg" data-testid={`consumo-total-${tandaKey}`}>
                {consumoTotal ?? '—'} <span className="text-xs text-gray-500">L</span>
              </div>
            </div>
            <div className="bg-neutral-950 rounded-md p-2">
              <div className="text-[10px] text-gray-500 uppercase">Vueltas Avg</div>
              <div className="font-mono text-yellow-400 text-lg" data-testid={`avg-laps-${tandaKey}`}>
                {avgLaps ?? '—'}
              </div>
            </div>
            <div className="bg-neutral-950 rounded-md p-2">
              <div className="text-[10px] text-gray-500 uppercase">Consumo / vta avg</div>
              <div className="font-mono text-yellow-400 text-lg" data-testid={`consumo-avg-${tandaKey}`}>
                {consumoPorAvgLap ?? '—'} <span className="text-xs text-gray-500">L</span>
              </div>
            </div>
          </div>

          {/* Race-need projection: how much fuel will be left after the race
              and what the car will weigh at scrutineering. */}
          <div className="pt-3 border-t border-neutral-800 space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
              <div>
                <Label className="text-xs text-gray-400">Combustible necesario carrera (L)</Label>
                <NumInput
                  value={fuel.race_needed}
                  onChange={(v) => setFuel({ ...fuel, race_needed: v })}
                  placeholder="Ej: 18"
                  testId={`race-needed-${tandaKey}`}
                />
              </div>
              <div className="bg-neutral-950 rounded-md p-2 text-center">
                <div className="text-[10px] text-gray-500 uppercase">Remanente a técnica</div>
                <div
                  className={`font-mono text-lg ${
                    remanenteTecnica === null
                      ? 'text-gray-500'
                      : remanenteTecnica < 0
                      ? 'text-red-400'
                      : 'text-orange-300'
                  }`}
                  data-testid={`remanente-tecnica-${tandaKey}`}
                >
                  {remanenteTecnica === null ? '—' : remanenteTecnica}
                  <span className="text-xs text-gray-500"> L</span>
                </div>
              </div>
              <div className="bg-neutral-950 rounded-md p-2 text-center">
                <div className="text-[10px] text-gray-500 uppercase">Peso a la técnica</div>
                <div
                  className="font-mono text-blue-400 text-lg"
                  data-testid={`peso-tecnica-${tandaKey}`}
                >
                  {pesoTecnica ?? '—'} <span className="text-xs text-gray-500">kg</span>
                </div>
              </div>
            </div>
            <p className="text-[10px] text-gray-500">
              Remanente = salida − necesario. Peso a la técnica = peso base + remanente × 0.74 kg/L.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Weight & Temperatures side-by-side on iPad */}
      <div className="grid md:grid-cols-2 gap-3">
        <Card className="bg-neutral-900 border-neutral-800">
          <CardContent className="p-4 space-y-3">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <Scale className="w-4 h-4 text-blue-500" />
              Peso del auto
            </h3>
            <div className="grid grid-cols-1 gap-2">
              <div>
                <Label className="text-xs text-gray-400">
                  Peso base sin combustible (kg)
                  {baseCarWeight != null && weights.override == null && (
                    <span className="ml-1 text-gray-600">· del evento: {baseCarWeight}</span>
                  )}
                </Label>
                <NumInput
                  value={weights.override}
                  onChange={(v) => setWeights({ ...weights, override: v })}
                  placeholder={baseCarWeight != null ? `Heredado: ${baseCarWeight}` : 'Sin peso base del evento'}
                  testId={`weight-override-${tandaKey}`}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-gray-400">
                    Peso reglamentario (kg)
                    {regulatoryWeight != null && weights.regulatory_override == null && (
                      <span className="ml-1 text-gray-600">· del evento: {regulatoryWeight}</span>
                    )}
                  </Label>
                  <NumInput
                    value={weights.regulatory_override}
                    onChange={(v) => setWeights({ ...weights, regulatory_override: v })}
                    placeholder={regulatoryWeight != null ? `Heredado: ${regulatoryWeight}` : 'Mínimo'}
                    testId={`weight-regulatory-override-${tandaKey}`}
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-400">
                    Lastre reglamentario (kg)
                    {regulatoryBallast != null && weights.ballast_override == null && (
                      <span className="ml-1 text-gray-600">· del evento: {regulatoryBallast}</span>
                    )}
                  </Label>
                  <NumInput
                    value={weights.ballast_override}
                    onChange={(v) => setWeights({ ...weights, ballast_override: v })}
                    placeholder={regulatoryBallast != null ? `Heredado: ${regulatoryBallast}` : 'Lastre'}
                    testId={`weight-ballast-override-${tandaKey}`}
                  />
                </div>
              </div>
              <p className="text-[10px] text-gray-500">
                Sólo completá si cambió en esta tanda. Salida = base × 1 + combustible × 0.74 kg/L.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2 pt-2 border-t border-neutral-800 text-center">
              <div className="bg-neutral-950 rounded-md p-2">
                <div className="text-[10px] text-gray-500 uppercase">Peso salida</div>
                <div className="font-mono text-blue-400 text-lg" data-testid={`peso-salida-${tandaKey}`}>
                  {pesoSalida ?? '—'} <span className="text-xs text-gray-500">kg</span>
                </div>
              </div>
              <div className="bg-neutral-950 rounded-md p-2">
                <div className="text-[10px] text-gray-500 uppercase">Peso regreso</div>
                <div className="font-mono text-blue-400 text-lg" data-testid={`peso-entrada-${tandaKey}`}>
                  {pesoEntrada ?? '—'} <span className="text-xs text-gray-500">kg</span>
                </div>
              </div>
              <div className="bg-neutral-950 rounded-md p-2">
                <div className="text-[10px] text-gray-500 uppercase">Margen reg.</div>
                <div
                  className={`font-mono text-lg ${
                    margenReglamentario === null
                      ? 'text-gray-500'
                      : margenReglamentario < 0
                      ? 'text-red-400'
                      : margenReglamentario < 5
                      ? 'text-yellow-400'
                      : 'text-green-400'
                  }`}
                  data-testid={`margen-reglamentario-${tandaKey}`}
                >
                  {margenReglamentario === null ? '—' : (margenReglamentario > 0 ? `+${margenReglamentario}` : margenReglamentario)}
                  <span className="text-xs text-gray-500"> kg</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-neutral-900 border-neutral-800">
          <CardContent className="p-4 space-y-2">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <Thermometer className="w-4 h-4 text-red-400" />
              Temperaturas (°C)
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                ['agua', 'Agua'],
                ['sonda', 'Sonda'],
                ['ambiente', 'Ambiente'],
                ['pista', 'Pista'],
              ].map(([k, lab]) => (
                <div key={k}>
                  <Label className="text-xs text-gray-400">{lab}</Label>
                  <NumInput
                    value={temps[k]}
                    onChange={(v) => setTemps({ ...temps, [k]: v })}
                    placeholder="°C"
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Observations & next changes */}
      <div className="grid md:grid-cols-2 gap-3">
        <Card className="bg-neutral-900 border-neutral-800">
          <CardContent className="p-4 space-y-2">
            <h3 className="text-base font-semibold">Observaciones (feeling del piloto)</h3>
            <Textarea
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              data-testid={`obs-${tandaKey}`}
              placeholder="Ej: el auto subvira en la 3, sobrevira saliendo de la última, frenos blandos..."
              className="min-h-[140px] bg-neutral-800 border-neutral-700 text-white text-base"
            />
          </CardContent>
        </Card>
        <Card className="bg-neutral-900 border-neutral-800">
          <CardContent className="p-4 space-y-2">
            <h3 className="text-base font-semibold">Cambios próxima tanda</h3>
            <Textarea
              value={changesNext}
              onChange={(e) => setChangesNext(e.target.value)}
              data-testid={`changes-${tandaKey}`}
              placeholder="Ej: subir 2 lbs del tren trasero, bajar altura 5mm, +1 vuelta de barra..."
              className="min-h-[140px] bg-neutral-800 border-neutral-700 text-white text-base"
            />
          </CardContent>
        </Card>
      </div>

      {/* Analysis import dialog */}
      {eventId && (
        <AnalysisImportDialog
          open={showImport}
          onOpenChange={setShowImport}
          eventId={eventId}
          eventDate={eventDate}
          tandaKey={tandaKey}
          tandaLabel={tandaLabel}
          pilotName={pilotName}
          eventCategory={eventCategory}
          eventCircuit={eventCircuit}
          onImported={handleImported}
        />
      )}
    </div>
  );
}
